import { Grid, Config, html } from 'gridjs';
import { IColumnSorting } from "./interfaces/column-sorting.js";
import { format } from '../utils/string.js';
import { appendUrlParams } from '../../components-shared/utils/url-utils.js';

export class CmsGrid extends HTMLElement {
    constructor(private config?: ICmsGridConfig) {
        super();

        if (config) {
            this.updateGridConfig();
        }
    }

    /* #region Temporary hack - pager buttons does postback */
    private fixButtonsTimeout: number;
    private fixPagerButtons() {
        if (this.fixButtonsTimeout) {
            window.clearTimeout(this.fixButtonsTimeout);
        }

        this.fixButtonsTimeout = window.setTimeout(() => {
            const buttons = Array.from(this.querySelectorAll<HTMLButtonElement>(".gridjs-pages button"));
            buttons.forEach(b => { b.type = "button"; });
        }, 500);
    }

    /* #endregion */

    /* #region Public */
    public onRowClicked: (row: any) => void;
    public onCellClicked: (cell: any, column: any, row: any) => void;

    public setConfig(config: ICmsGridConfig) {
        this.config = config;

        this.updateGridConfig();

        if (this.isInitialized) {
            this.updateGrid();
        } else {
            this.createGrid();
        }
    }

    public sort(sorting: IColumnSorting) {
        const column = this.config.columns.find(c => c.dataKey === sorting.dataKey);
        const columnIndex = this.config.columns.indexOf(column);
        const updatedColumnHeader = document.querySelector(`th:nth-child(${columnIndex})`) as HTMLTableRowElement;

        updatedColumnHeader.click();

        if (sorting.direction === 'desc') {
            updatedColumnHeader.click();
        }
    }

    /* #endregion */

    /* #region Private */
    private grid: Grid;
    private gridConfig: Config;
    private isInitialized: boolean = false;

    private updateGridConfig() {
        this.gridConfig = {
            columns: this.createGridConfigColumns(),
            language: this.createGridConfigLanguage(),
            server: this.createGridConfigServer(),
            sort: this.createGridConfigSort(),
            pagination: this.createGridConfigPagination()
        } as Config;
    }

    private createGridConfigColumns() {
        return this.config.columns.map(c => {
            return {
                id: c.dataKey,
                name: c.label,
                hidden: c.hidden,
                sort: c.sortable,
                formatter: c.html === true && c.formatter ? (cell: any, row: any) => html(c.formatter(cell, row)) : c.formatter
            }
        });
    }

    private createGridConfigLanguage() {
        return {
            sort: {
                sortAsc: this.config.labels.sortAsc,
                sortDesc: this.config.labels.sortDesc,
            },
            pagination: {
                previous: this.config.labels.previous,
                next: this.config.labels.next,
                navigate: (page: number, pages: number) => format(this.config.labels.navigate, page.toString(), pages.toString()),
                page: (page: number) => format(this.config.labels.page, page.toString()),
                showing: this.config.labels.showing,
                of: this.config.labels.of,
                to: this.config.labels.to,
                results: this.config.labels.results,
            },
            loading: this.config.labels.loading,
            noRecordsFound: this.config.labels.notFoundMessage,
            error: this.config.labels.errorMessage,
        } as Config['language'];
    }

    private createGridConfigServer() {
        return {
            url: this.config.apiBaseUrl,
            then: this.config.apiDataMapper,
            total: this.config.apiDataTotalMapper
        } as Config['server'];
    }
    
    private createGridConfigSort() {
        return {
            enabled: this.config.sortable,
            multiColumn: false,
            server: {
                url: (prevUrl: any, columns: any) => {
                    if (!columns.length) return prevUrl;
                    const col = columns[0];
                    const dir = col.direction === 1 ? 'asc' : 'desc';
                    const colName = this.config.columns[col.index];
                    this.fixPagerButtons();
                    return appendUrlParams(prevUrl, { sortBy: colName.dataKey, sortDir: dir });
                }
            }
        } as Config['sort'];
    }

    private createGridConfigPagination() {
        return {
            enabled: this.config.pagination.enabled,
            resetPageOnUpdate: false,
            limit: this.config.pagination.itemsPerPage,
            server: {
                url: (prevUrl: string, page: number, limit: number) => appendUrlParams(prevUrl, { page: page, count: limit })
            }
        } as Config['pagination'];
    }

    private createGrid() {
        this.grid = new Grid(this.gridConfig).render(this);
        this.grid.on("rowClick", (_: any, row: any) => {
            if (this.onRowClicked) {
                this.onRowClicked(row);
            }
        });

        this.grid.on("cellClick", (_: any, cell: any, column: any, row: any) => {
            if (this.onCellClicked) {
                this.onCellClicked(cell, column, row);
            }
        });
        
        this.fixPagerButtons();
    }

    private updateGrid() {
        this.grid.config.server.url = this.config.apiBaseUrl;
        this.grid.forceRender();

        this.fixPagerButtons();
    }

    /* #endregion */

    connectedCallback() {
        if (this.isInitialized) {
            return;
        }
        
        if (this.config) {
            this.isInitialized = true;
            this.createGrid();
        }
    }
}

export interface ICmsGridConfig {
    apiBaseUrl: string;
    apiDataMapper: (data: any) => any[];
    apiDataTotalMapper: (data: any) => number;
    columns: ICmsGridColumn[];
    sortable: boolean;
    defaultSort?: IColumnSorting;
    pagination: ICmsGridPaginationConfig;
    labels: ICmsGridLabels;
}

export interface ICmsGridColumn {
    dataKey: string;
    label: string;
    sortable: boolean;
    hidden: boolean;
    html?: boolean;
    formatter?: (cell: any, row: any) => string;
}

export interface ICmsGridPaginationConfig {
    enabled: boolean;
    itemsPerPage: number;
}

export interface ICmsGridLabels {
    sortAsc: string;
    sortDesc: string;
    previous: string;
    next: string;
    navigate: string;
    page: string;
    showing: string;
    of: string;
    to: string;
    results: string;
    loading: string;
    notFoundMessage: string;
    errorMessage: string;
}

customElements.define('cms-grid', CmsGrid);